import {useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {NotificationManager} from 'react-notifications';
import DragAndDrop from '../../common/simple/dragAndDrop';
import Button from '../../common/Button';
import fileUpload from '../../images/newDesign/fileUpload.svg';
import {useAccessToken} from "../../common/hooks/useAccessToken";
import {UploadListItem} from "./UploadListItem";
import {UploadItem} from "./UploadItem";
import {UploadFileStyled} from "./UploadFile.styled";
import {UploadFailedModal} from "./UploadFailedModal";
import {InsuranceModal} from './InsuranceModal';
import {ModalManager} from "../../ui/ModalManager";
import {FileSize} from "../../ui/FileSize";
import { isAdminSelector } from '../../store/auth/selectors';
import {constants} from "../../common/constants/constants";

const FILE_SIZE_LIMIT = 7340032; // IEC 7MB

export function UploadFile({ archiveFiles, onDone = () => void null, onCancel = () => void null, onFail }) {

    const { t } = useTranslation();
    const client = useAccessToken();

    const isAdmin = useSelector(isAdminSelector);
    const buildingID = useSelector(state => state.buildings.managerData.buildingID);
    const userID = useSelector(state => state.auth.userProfile.userID);
    const userProfile = useSelector(state => state.auth.userProfile);

    const [uploadItems, setUploadItems] = useState(/** @type {UploadItem[]} */ []);
    const inputFile = useRef(null);

    /**
     * @param {FileList | File[]} files
     */
    function addFiles(files) {
        const filesArray = Array.isArray(files) ? files : Array.from(files);
        const filtered = filesArray.filter(f => {
            const exist = uploadItems.some(item => item.file.name === f.name);
            const oversized = f.size > FILE_SIZE_LIMIT;
            if (exist) console.log('File duplicate, skipping', f.name);
            if (oversized) console.log('File is too big, skipping', f.name);
            return !exist && !oversized;
        });
        setUploadItems([...uploadItems, ...(filtered.map(file => new UploadItem(file, userID))) ]);
    }

    function handleItemDelete(item) {
        setUploadItems(uploadItems.filter(i => i.id !== item.id));
    }

    function handleFileSelected(e) {
        const files = e.target.files;
        addFiles(files);
        e.target.value = '';
    }

    /**
     * @param {ProgressEvent} e
     * @param {ArchiveUploadItem} item
     * */
    function handleUploadProgress(e, item) {
        const uploadItem = uploadItems.find(i => i.id === item.id);
        uploadItem.setUploadProgress(e.loaded);
    }

    async function handleUploadDone(resp, err, item) {
        const uploadItem = uploadItems.find(i => i.id === item.id);
        if (uploadItem.metadata.type === "insurance" && isAdmin) {
            await ModalManager.open(<InsuranceModal item={item} onConfirm={onConfirm}/>);
        }
        if (err == null) {
            uploadItem.setUploadSuccess(resp);
        } else {
            uploadItem.setUploadError(err);
        }
        const done = uploadItems.filter(item => item.done);
        if (done.length === uploadItems.length) {
            const failed = uploadItems.filter(item => !item.success);
            if (failed.length > 0) {
                for (const item of failed) {
                    await ModalManager.open(<UploadFailedModal item={item}/>);
                }
                onFail();
                setUploadItems([]);
            } else {
                onDone();
            }
        }
    }
    const onConfirm = async (close) => {
        const query = `?buildingID=${buildingID}`;
        const params = {
            sendTo: ["ariel@bllink.co", "ohad@bllink.co", "rayee@bllink.co"],
            title:  'subject: New Insurance - building: <buildingAddress>',
            content: 'New insurance offer needed <br>' +
                     'Building: <buildingAddress> <br>' +
                     'User: <userID>',
            roles: userProfile.roles
        };
        await client.tenants.utils.postInternalEmail(constants.backendEndpoints.GENERIC_INTERNAL_EMAIL_ENDPOINT ,query , params);
        close();
    }
    async function handleUploadClick() {
        uploadItems.forEach(item => item.setUploadProgress(0)); // make visible change instantly
        for (const item of uploadItems) {
            if (!item.metadata.type) {
                NotificationManager.error(t('archive.select_type', {name: item.file.name}))

                delete item.loaded
                return
            }
        }
        const { files: signedItems } = await client.managers.archive.generateUploadLinks(buildingID, uploadItems);
        for (const signedItem of signedItems) {
            const uploadItem = uploadItems.find(i => i.id === signedItem.id);
            if (signedItem.exist == null) {
                client.managers.archive.uploadFile(signedItem, uploadItem.file, handleUploadProgress, handleUploadDone);
            } else {
                const splitByDot = signedItem.path.split('.');
                const extension = splitByDot.pop(1);
                const noExtension = splitByDot.join('.');
                let counter = 0;
                let exist = true;
                while (exist) {
                    const suffix = `-${++counter}.${extension}`;
                    exist = archiveFiles.some(f => f.Key.startsWith(noExtension) && f.Key.endsWith(suffix));
                }
                uploadItem.addNameSuffix(counter);
                const { files: reSignedItems } = await client.managers.archive.generateUploadLinks(buildingID, [uploadItem]);
                client.managers.archive.uploadFile(reSignedItems[0], uploadItem.file, handleUploadProgress, handleUploadDone);
            }
        }
    }

    return (
        <UploadFileStyled>
            <input
                type='file'
                ref={inputFile}
                accept=".pdf, .doc, .docx, .xml"
                style={{display: 'none'}}
                multiple={true}
                onChange={handleFileSelected}
            />
            <DragAndDrop handleDrop={e => addFiles(e.dataTransfer.files)}>
                <div className="drag-and-drop-field" onClick={() => inputFile.current.click()}>
                    <img src={fileUpload} />
                    <div className='bllink-heading3'>{t('archive.upload_hint')}</div>
                    <div>{t('archive.upload_limits', { maxFileSize: (<FileSize>{FILE_SIZE_LIMIT}</FileSize>) })}</div>
                </div>
            </DragAndDrop>

            {uploadItems.length > 0 && (<>
                <div className="files-list">
                    {uploadItems.map((item) => <UploadListItem item={item} key={item.id} onDelete={handleItemDelete} />)}
                </div>
                <Button onClick={handleUploadClick}>{t('payment_settings.uploadFile')}</Button>
            </>)}

            <button type="button" className="link-button bllink-text-tiny" onClick={() => onCancel()} >{t('archive.go_back_to_list')}</button>
        </UploadFileStyled>
    )
}

